import type { ProductRankEntry, ProductRow, ProductsReport } from '../../types/reports';
import type { Segment } from '../reportsView';
import { formatMoney } from '../ownerOverview';
import { APPROXIMATE_NOTE, intLabel, moneyLabel, percentLabel, plural, strong, text, UNKNOWN, type RowView, type SectionView, type StatView } from './shared';

/**
 * P-084 · R3 «¿Qué productos me convienen?» — presentación pura. Cuatro listas separadas (más vendidos, más ingreso,
 * más margen conocido, mejor margen %), sin un score único ni recomendaciones. Lo que no tiene costo suficiente queda
 * fuera de las conclusiones de margen y se explica; lo aproximado se declara.
 */
export interface ProductsView {
  say: Segment[];
  stats: StatView[];
  rankings: Array<{ key: string; title: string; rows: RowView[]; empty: string }>;
  marginNote: string | null;
  detail: SectionView[];
  notes: string[];
  empty: { title: string; description: string } | null;
}

export const PRODUCTS_EMPTY_TITLE = 'No hubo productos vendidos en este periodo.';
export const PRODUCTS_EMPTY_TEXT = 'Cuando se registren ventas con productos, aquí verás cuáles se venden más y cuáles dejan más.';
export const PRODUCTS_REFUNDS_NOTE = 'Las anuladas no cuentan; las devoluciones restan al producto.';
export const PRODUCTS_MARGIN_RULE_NOTE = 'El margen solo se calcula donde el costo cubre al menos 9 de cada 10 pesos vendidos de ese producto.';
export const PRODUCTS_NO_ADVICE_NOTE = 'Este reporte no recomienda precios ni dejar de vender nada: te dice qué pasó.';

export function productsReportView(report: ProductsReport): ProductsView {
  const { totals, margin, rankings } = report;
  const notes = [PRODUCTS_REFUNDS_NOTE, PRODUCTS_MARGIN_RULE_NOTE];
  if (margin.hasApproximateCost) notes.push(APPROXIMATE_NOTE);
  if (margin.note) notes.push(margin.note);
  notes.push(PRODUCTS_NO_ADVICE_NOTE);

  if (totals.products === 0) {
    return { say: [text(PRODUCTS_EMPTY_TITLE)], stats: statsOf(report), rankings: [], marginNote: null, detail: [], notes, empty: { title: PRODUCTS_EMPTY_TITLE, description: PRODUCTS_EMPTY_TEXT } };
  }

  const say: Segment[] = [];
  const topRevenue = rankings.revenue[0];
  const topMargin = rankings.margin[0];
  const topUnits = rankings.units[0];
  if (topRevenue) say.push(strong(topRevenue.name), text(` es tu producto que más ingreso genera (${topRevenue.valueLabel}).`));
  if (topMargin) say.push(text(topRevenue ? ' ' : ''), strong(topMargin.name), text(` es el que más margen conocido aporta (${topMargin.valueLabel}).`));
  const unknownBestSeller = topUnits && !rankings.margin.some((entry) => entry.key === topUnits.key) && report.products.find((row) => row.key === topUnits.key)?.knownMargin === null;
  if (unknownBestSeller) say.push(text(' '), strong(topUnits!.name), text(' vende mucho, pero no tenemos costo suficiente para decir cuánto deja.'));
  if (!topRevenue && !topMargin && topUnits) say.push(strong(topUnits.name), text(` es tu producto más vendido (${topUnits.valueLabel}).`));
  if (say.length === 0) say.push(text('Hubo ventas con productos, pero sin importes atribuibles por producto.'));

  const marginNote = !margin.published
    ? margin.reason
    : totals.productsWithMargin === 0
      ? 'Ningún producto tiene costo suficiente para hablar de su margen.'
      : totals.productsWithMargin < totals.products
        ? `Con margen conocido: ${intLabel(totals.productsWithMargin)} de ${intLabel(totals.products)} productos. Los demás no tienen costo suficiente.`
        : null;

  const rank = (key: string, title: string, entries: ProductRankEntry[], empty: string, extra: (entry: ProductRankEntry, row: ProductRow | undefined) => RowView['figures']) => ({
    key,
    title,
    rows: entries.map((entry) => {
      const row = report.products.find((product) => product.key === entry.key);
      return { key: entry.key, title: entry.name, subtitle: null, meta: row && row.channel === 'loyverse' && report.products.some((other) => other.channel === 'native') ? ['Loyverse'] : [], value: entry.valueLabel, valueTone: 'default' as const, badge: entry.approximate ? { text: 'aprox.', tone: 'warning' as const } : null, figures: extra(entry, row), destination: null };
    }),
    empty,
  });
  const rankingsView = [
    rank('units', 'Más vendidos', rankings.units, 'Sin unidades vendidas.', (_entry, row) => (row && row.revenue !== null ? [{ label: 'Ingreso', value: formatMoney(row.revenue) }] : [])),
    rank('revenue', 'Más ingreso generan', rankings.revenue, 'Sin ingreso atribuible por producto.', (_entry, row) => (row ? [{ label: 'Unidades', value: intLabel(row.units) }] : [])),
    rank('margin', 'Más margen conocido aportan', rankings.margin, margin.published ? 'Ningún producto con costo suficiente para calcular su margen.' : (margin.reason ?? 'Sin margen publicable.'), (_entry, row) => (row && row.knownMarginPercent !== null ? [{ label: 'Margen', value: percentLabel(row.knownMarginPercent) }] : [])),
    rank('marginPercent', 'Mejor margen %', rankings.marginPercent, margin.published ? `Hacen falta al menos ${margin.minUnitsForPercent} unidades vendidas y costo completo.` : (margin.reason ?? 'Sin margen publicable.'), (_entry, row) => (row && row.knownMargin !== null ? [{ label: 'Margen', value: formatMoney(row.knownMargin) }] : [])),
  ];

  const all: RowView[] = report.products.map((row) => ({
    key: row.key,
    title: row.name,
    subtitle: row.knownMargin !== null ? null : row.marginNote,
    meta: [`${intLabel(row.units)} ${plural(row.units, 'unidad', 'unidades')}`, ...(row.refundedUnits > 0 ? [`${intLabel(row.refundedUnits)} ${plural(row.refundedUnits, 'devuelta', 'devueltas')}`] : []), ...(report.products.some((other) => other.channel !== row.channel) ? [row.channel === 'loyverse' ? 'Loyverse' : 'Punto de venta'] : [])],
    value: moneyLabel(row.revenue),
    valueTone: 'default',
    badge: row.knownMargin !== null ? { text: `Deja ${formatMoney(row.knownMargin)}${row.approximate ? ' aprox.' : ''}`, tone: 'success' } : row.costQuality === 'none' ? { text: 'Sin costo', tone: 'neutral' } : { text: `Costo ${Math.round(row.coveragePercent)} %`, tone: 'neutral' },
    figures: row.knownMarginPercent !== null ? [{ label: 'Margen', value: percentLabel(row.knownMarginPercent) }] : [],
    destination: null,
  }));
  const detail: SectionView[] = [{
    key: 'all',
    title: 'Todos los productos del periodo',
    rows: all,
    empty: null,
    note: totals.unattributedRevenue !== null && Number(totals.unattributedRevenue) !== 0
      ? `Además, ${formatMoney(totals.unattributedRevenue)} ${Number(totals.unattributedRevenue) > 0 ? 'entraron' : 'salieron'} sin producto o sin detalle (ingresos manuales, devoluciones o líneas sin importe): por eso la suma de productos no es igual a «Entró».`
      : null,
    more: report.moreCount > 0 ? `${report.moreCount} ${plural(report.moreCount, 'producto más', 'productos más')}.` : null,
  }];

  return { say, stats: statsOf(report), rankings: rankingsView, marginNote, detail, notes, empty: null };
}

function statsOf(report: ProductsReport): StatView[] {
  const { totals } = report;
  return [
    { key: 'products', label: 'Productos vendidos', value: intLabel(totals.products), hint: null, tone: 'default' },
    { key: 'units', label: 'Unidades', value: intLabel(totals.units), hint: null, tone: 'default' },
    { key: 'costed', label: 'Con costo conocido', value: `${intLabel(totals.productsWithCompleteCost)} de ${intLabel(totals.products)}`, hint: totals.attributedRevenue === null ? null : `${Math.round(totals.coveragePercent)} % del ingreso`, tone: totals.productsWithCompleteCost === totals.products ? 'default' : 'warning' },
    { key: 'unattributed', label: 'Sin producto', value: totals.unattributedRevenue === null ? UNKNOWN : Number(totals.unattributedRevenue) === 0 ? formatMoney('0.00') : formatMoney(totals.unattributedRevenue), hint: totals.unattributedRevenue !== null && Number(totals.unattributedRevenue) !== 0 ? 'ingresos manuales o sin detalle' : 'todo tiene producto', tone: totals.unattributedRevenue !== null && Number(totals.unattributedRevenue) !== 0 ? 'muted' : 'default' },
  ];
}
